import React, { useEffect, useState } from "react";
import api, { formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from "@/components/ui/command";
import { toast } from "sonner";

const RELATIONSHIP_TYPES = [
    { value: "owner", label: "Owner" },
    { value: "tenant", label: "Tenant" },
    { value: "family", label: "Family Member" },
    { value: "other", label: "Other Occupant" },
];
const emptyNewForm = { name: "", email: "", phone: "" };

/** Add Resident to a flat — either link an EXISTING resident (searched from the existing
 * Residents/Users database, never creates a duplicate record) or create a brand new one
 * (reusing the exact same admin-only creation logic as the existing Residents page). */
export default function AddResidentDialog({ open, onOpenChange, flatId, onSaved }) {
    const { user } = useAuth();
    const isAdmin = user?.role === "admin";
    const [tab, setTab] = useState("existing");
    const [allUsers, setAllUsers] = useState([]);
    const [selectedResident, setSelectedResident] = useState(null);
    const [relationshipType, setRelationshipType] = useState("family");
    const [moveInDate, setMoveInDate] = useState("");
    const [primary, setPrimary] = useState(false);
    const [newForm, setNewForm] = useState(emptyNewForm);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (!open) return;
        setTab("existing"); setSelectedResident(null); setRelationshipType("family");
        setMoveInDate(""); setPrimary(false); setNewForm(emptyNewForm);
        api.get("/users").then(r => setAllUsers(r.data || [])).catch(() => { });
    }, [open]);

    async function submitExisting(e) {
        e.preventDefault();
        if (!selectedResident) { toast.error("Select a resident to add"); return; }
        setBusy(true);
        try {
            await api.post(`/flats/${flatId}/residents`, {
                resident_id: selectedResident.id, relationship_type: relationshipType,
                move_in_date: moveInDate || null, is_primary_contact: primary,
            });
            toast.success("Resident linked to flat");
            onOpenChange(false);
            onSaved?.();
        } catch (e) { toast.error(formatError(e)); }
        finally { setBusy(false); }
    }

    async function submitNew(e) {
        e.preventDefault();
        setBusy(true);
        try {
            await api.post(`/flats/${flatId}/residents/new`, {
                ...newForm, relationship_type: relationshipType, move_in_date: moveInDate || null, is_primary_contact: primary,
            });
            toast.success("Resident created and linked");
            onOpenChange(false);
            onSaved?.();
        } catch (e) { toast.error(formatError(e)); }
        finally { setBusy(false); }
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="rounded-sm max-w-lg max-h-[85vh] overflow-y-auto" data-testid="add-resident-dialog">
                <DialogHeader>
                    <DialogTitle className="font-heading text-2xl tracking-tight">Add Resident</DialogTitle>
                </DialogHeader>
                <Tabs value={tab} onValueChange={setTab}>
                    <TabsList className="grid grid-cols-2 w-full">
                        <TabsTrigger data-testid="add-resident-tab-existing" value="existing">Existing Resident</TabsTrigger>
                        <TabsTrigger data-testid="add-resident-tab-new" value="new">New Resident</TabsTrigger>
                    </TabsList>

                    <TabsContent value="existing">
                        <form onSubmit={submitExisting} className="space-y-4 pt-3">
                            <div className="space-y-2">
                                <Label>Search resident *</Label>
                                {selectedResident ? (
                                    <div className="flex items-center justify-between border border-brand-line rounded-sm p-2.5 bg-brand-sage/40" data-testid="add-resident-selected">
                                        <div>
                                            <p className="text-sm font-medium text-brand-ink">{selectedResident.name}</p>
                                            <p className="text-xs text-brand-inkSoft">{selectedResident.email}</p>
                                        </div>
                                        <button type="button" data-testid="add-resident-clear-selection" onClick={() => setSelectedResident(null)} className="text-xs text-brand-action hover:underline">Change</button>
                                    </div>
                                ) : (
                                    <Command className="rounded-sm border border-brand-line" data-testid="add-resident-search">
                                        <CommandInput placeholder="Search by name or email..." />
                                        <CommandList className="max-h-48">
                                            <CommandEmpty>No residents found.</CommandEmpty>
                                            <CommandGroup>
                                                {allUsers.map(u => (
                                                    <CommandItem key={u.id} value={`${u.name} ${u.email}`}
                                                        data-testid={`add-resident-option-${u.id}`} onSelect={() => setSelectedResident(u)}>
                                                        <span className="font-medium">{u.name}</span>
                                                        <span className="text-brand-inkSoft ml-2 text-xs">{u.email}</span>
                                                    </CommandItem>
                                                ))}
                                            </CommandGroup>
                                        </CommandList>
                                    </Command>
                                )}
                            </div>
                            <RelationshipFields relationshipType={relationshipType} setRelationshipType={setRelationshipType}
                                moveInDate={moveInDate} setMoveInDate={setMoveInDate} primary={primary} setPrimary={setPrimary} />
                            <DialogFooter>
                                <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="rounded-full">Cancel</Button>
                                <Button type="submit" data-testid="add-resident-submit-existing" disabled={busy} className="rounded-full bg-brand-action hover:bg-brand-actionHover">Add to flat</Button>
                            </DialogFooter>
                        </form>
                    </TabsContent>

                    <TabsContent value="new">
                        {!isAdmin ? (
                            <p className="text-sm text-brand-inkSoft py-8 text-center" data-testid="add-resident-new-admin-only">Only Admin can create a new resident account. Ask an Admin, or add an existing resident instead.</p>
                        ) : (
                            <form onSubmit={submitNew} className="space-y-4 pt-3">
                                <div className="grid grid-cols-2 gap-3">
                                    <div className="space-y-2"><Label>Full name *</Label>
                                        <Input data-testid="add-resident-new-name" required value={newForm.name} onChange={e => setNewForm({ ...newForm, name: e.target.value })} className="rounded-sm border-brand-line" />
                                    </div>
                                    <div className="space-y-2"><Label>Phone</Label>
                                        <Input data-testid="add-resident-new-phone" value={newForm.phone} onChange={e => setNewForm({ ...newForm, phone: e.target.value })} className="rounded-sm border-brand-line" />
                                    </div>
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    <div className="space-y-2"><Label>Email *</Label>
                                        <Input data-testid="add-resident-new-email" type="email" required value={newForm.email} onChange={e => setNewForm({ ...newForm, email: e.target.value })} className="rounded-sm border-brand-line" />
                                    </div>
                                </div>
                                <div className="text-xs text-brand-inkSoft bg-brand-sage/40 border border-brand-line rounded-sm p-2.5" data-testid="add-resident-default-password-note">
                                    This resident starts on the default password <span className="font-mono font-medium text-brand-ink">Welcome@123</span> and will be required to set their own on first login.
                                </div>
                                <RelationshipFields relationshipType={relationshipType} setRelationshipType={setRelationshipType}
                                    moveInDate={moveInDate} setMoveInDate={setMoveInDate} primary={primary} setPrimary={setPrimary} />
                                <DialogFooter>
                                    <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="rounded-full">Cancel</Button>
                                    <Button type="submit" data-testid="add-resident-submit-new" disabled={busy} className="rounded-full bg-brand-action hover:bg-brand-actionHover">Create & add to flat</Button>
                                </DialogFooter>
                            </form>
                        )}
                    </TabsContent>
                </Tabs>
            </DialogContent>
        </Dialog>
    );
}

function RelationshipFields({ relationshipType, setRelationshipType, moveInDate, setMoveInDate, primary, setPrimary }) {
    return (
        <>
            <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2"><Label>Relationship Type *</Label>
                    <Select value={relationshipType} onValueChange={setRelationshipType}>
                        <SelectTrigger data-testid="add-resident-relationship-type" className="rounded-sm border-brand-line"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            {RELATIONSHIP_TYPES.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                        </SelectContent>
                    </Select>
                </div>
                <div className="space-y-2"><Label>Move-in Date</Label>
                    <Input data-testid="add-resident-move-in-date" type="date" value={moveInDate} onChange={e => setMoveInDate(e.target.value)} className="rounded-sm border-brand-line" />
                </div>
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
                <input data-testid="add-resident-primary-checkbox" type="checkbox" checked={primary} onChange={e => setPrimary(e.target.checked)} className="h-4 w-4 rounded border-brand-line accent-brand-action" />
                <span className="text-sm text-brand-ink">Set as Primary Contact</span>
            </label>
        </>
    );
}
